import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Button } from "../ui/button";
import { Loader2, FileText, Calendar, Gauge, X, Download, FileSpreadsheet } from "lucide-react";
import { maintenanceService } from '../../services/maintenance/planMaestroService';

interface HistoryItem {
    id: number;
    codigo_ot: string;
    rutina_nombre: string;
    tipo: string;
    kilometraje: number;
    fecha_realizado: string;
    costo_total: number;
}

interface PlanMatrix {
    nombre: string;
    descripcion: string;
    rutinas: { id: number; nombre: string; frecuencia_km: number; tipo: string }[];
    items_matrix: any[];
}

interface VehicleHistoryModalProps {
    open: boolean;
    onClose: () => void;
    vehiculoId: string;
    placa: string;
    marca?: string;
    modelo?: string;
    onViewOt?: (otId: number) => void;
}

export const VehicleHistoryModal: React.FC<VehicleHistoryModalProps> = ({ open, onClose, vehiculoId, placa, marca, modelo, onViewOt }) => {
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [planMatrix, setPlanMatrix] = useState<PlanMatrix | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const response = await maintenanceService.getVehicleHistory(vehiculoId);
            setHistory(response.data);
            if (response.plan) {
                setPlanMatrix(response.plan);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && vehiculoId) {
            fetchHistory();
        }
    }, [open, vehiculoId]);

    if (!open) return null;

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
    };

    const formatDay = (dateStr: string) => {
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('es-PE', { weekday: 'long' }).format(date);
    };

    const generatePDF = () => {
        const doc = new jsPDF();

        // --- PAGE 1: HISTORY ---
        // Header
        doc.setFontSize(20);
        doc.setTextColor(40);
        doc.text("VILOX MANTENIMIENTO", 14, 22);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`HISTORIAL DE MANTENIMIENTO: ${placa} - ${marca || ''} ${modelo || ''}`, 14, 30);
        doc.text(`Fecha Reporte: ${new Date().toLocaleDateString()}`, 14, 35);

        // History Table
        const tableBody = history.map((item) => [
            formatDate(item.fecha_realizado),
            item.rutina_nombre || item.tipo,
            item.codigo_ot,
            `${item.kilometraje.toLocaleString()} km`,
            `S/ ${(Number(item.costo_total) || 0).toFixed(2)}`
        ]);

        autoTable(doc, {
            startY: 40,
            head: [['Fecha', 'Actividad / Rutina', 'OT', 'Kilometraje', 'Costo']],
            body: tableBody,
            theme: 'striped',
            headStyles: { fillColor: [30, 41, 59] }, // Slate-800
            columnStyles: {
                4: { halign: 'right', fontStyle: 'bold' }
            }
        });

        // Totals
        const totalCost = history.reduce((acc, curr) => acc + (Number(curr.costo_total) || 0), 0);

        doc.setFontSize(12);
        doc.setTextColor(40);
        doc.text(`COSTO TOTAL ACUMULADO: S/ ${totalCost.toFixed(2)}`, 14, (doc as any).lastAutoTable.finalY + 15);

        // --- PAGE 2: PLAN MATRIX (If available) ---
        if (planMatrix && planMatrix.rutinas.length > 0) {
            doc.addPage();
            doc.setPage(2);

            // Header Page 2
            doc.setFontSize(14);
            doc.setTextColor(40);
            doc.text("PROGRAMA DE MANTENIMIENTO PREVENTIVO", 14, 20);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Plan Maestro: ${planMatrix.nombre}`, 14, 26);

            // Columns Construction
            const routineColumns = planMatrix.rutinas.map(r => ({
                header: r.nombre.toUpperCase(),
                dataKey: `rutina_${r.id}`
            }));

            const columns = [
                { header: 'ITEM / REPUESTO / SERVICIO', dataKey: 'nombre' },
                { header: 'UND', dataKey: 'unidad_medida' },
                ...routineColumns
            ];

            // Rows Construction with 'X' marking
            const rows = planMatrix.items_matrix.map((item: any) => {
                const rowData: any = {
                    nombre: item.nombre,
                    unidad_medida: item.unidad_medida || 'UND'
                };

                planMatrix.rutinas.forEach(r => {
                    const qty = item[`rutina_${r.id}`];
                    rowData[`rutina_${r.id}`] = qty ? (qty > 1 ? qty : 'X') : '';
                });

                return rowData;
            });

            autoTable(doc, {
                startY: 35,
                head: [columns.map(c => c.header)],
                body: rows.map(r => columns.map(c => r[c.dataKey])),
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 1.5, valign: 'middle' },
                headStyles: {
                    fillColor: [15, 23, 42], // Slate-900 (Black-ish)
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    lineWidth: 0.1,
                    lineColor: [200, 200, 200]
                },
                columnStyles: {
                    0: { cellWidth: 'auto', fontStyle: 'bold' }, // Item Name
                    1: { cellWidth: 15, halign: 'center' }, // Unit
                    // Routine columns auto-width
                },
                didParseCell: (data) => {
                    // Center align the checkmarks
                    if (data.section === 'body' && data.column.index >= 2) {
                        data.cell.styles.halign = 'center';
                        if (data.cell.raw === 'X') {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });

            // Disclaimer footer
            doc.setFontSize(8);
            doc.setTextColor(150);
            const finalY = (doc as any).lastAutoTable.finalY + 10;
            doc.text("Nota: 'X' indica que el ítem está incluido en la rutina. Números indican cantidad requerida > 1.", 14, finalY);
        }

        return doc;
    };

    const exportToPDF = () => {
        const doc = generatePDF();
        doc.save(`Historial_${placa}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const previewPDF = () => {
        const doc = generatePDF();
        window.open(doc.output('bloburl'), '_blank');
    };

    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Historial Mantenimiento');

        // Columns
        worksheet.columns = [
            { header: 'FECHA', key: 'fecha', width: 15 },
            { header: 'ACTIVIDAD / RUTINA', key: 'actividad', width: 40 },
            { header: 'CÓDIGO OT', key: 'ot', width: 15 },
            { header: 'TIPO', key: 'tipo', width: 15 },
            { header: 'KILOMETRAJE', key: 'km', width: 15 },
            { header: 'COSTO', key: 'costo', width: 15 },
        ];

        // Header Styles
        const titleRow = worksheet.addRow([`HISTORIAL DE MANTENIMIENTO - ${placa}`]);
        titleRow.font = { name: 'Arial Black', size: 14, color: { argb: 'FFFFFFFF' } };
        titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } }; // Slate-900
        worksheet.mergeCells(`A${titleRow.number}:F${titleRow.number}`);
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const subTitleRow = worksheet.addRow([`VEHÍCULO: ${marca || ''} ${modelo || ''} - Generado: ${new Date().toLocaleDateString()}`]);
        subTitleRow.font = { bold: true, size: 10, color: { argb: 'FF475569' } }; // Slate-600
        worksheet.mergeCells(`A${subTitleRow.number}:F${subTitleRow.number}`);
        subTitleRow.getCell(1).alignment = { horizontal: 'center' };

        worksheet.addRow([]); // Spacer

        // Table Header
        const headerRow = worksheet.getRow(4); // Adjust if rows added above change
        headerRow.values = ['FECHA', 'ACTIVIDAD / RUTINA', 'CÓDIGO OT', 'TIPO', 'KILOMETRAJE', 'COSTO'];
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }; // Slate-700
            cell.alignment = { horizontal: 'center' };
        });

        // Data
        let totalCost = 0;
        history.forEach((item) => {
            const costo = Number(item.costo_total) || 0;
            totalCost += costo;

            const row = worksheet.addRow([
                new Date(item.fecha_realizado).toLocaleDateString(),
                item.rutina_nombre || item.tipo,
                item.codigo_ot,
                item.tipo,
                item.kilometraje,
                costo
            ]);

            row.getCell(5).numFmt = '#,##0 "km"';
            row.getCell(6).numFmt = '"S/" #,##0.00';

            // Highlight Corrective Maintenance
            if (item.tipo === 'CORRECTIVO') {
                row.getCell(4).font = { color: { argb: 'FFE11D48' }, bold: true }; // Rose-600
            } else {
                row.getCell(4).font = { color: { argb: 'FF059669' }, bold: true }; // Emerald-600
            }
        });

        // Footer Total
        worksheet.addRow([]);
        const totalRow = worksheet.addRow(["", "", "", "", "TOTAL ACUMULADO:", totalCost]);
        totalRow.getCell(5).font = { bold: true };
        totalRow.getCell(6).font = { bold: true, color: { argb: 'FF1E293B' }, size: 12 };
        totalRow.getCell(6).numFmt = '"S/" #,##0.00';

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Historial_${placa}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-950 rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Historial de Mantenimiento</h2>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                Registro de actividades para el vehículo <span className="font-bold text-slate-900 dark:text-slate-200">{placa}</span>
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
                            <p className="text-slate-500 text-sm font-medium">Cargando historial...</p>
                        </div>
                    ) : history.length === 0 ? (
                        <div className="text-center py-20 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <p className="text-slate-500 font-bold text-lg">Sin registros anteriores</p>
                            <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
                                Las actividades aparecerán aquí una vez que se completen las Órdenes de Trabajo para este vehículo.
                            </p>
                        </div>
                    ) : (
                        <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 dark:bg-slate-900">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Fecha</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Actividad / Rutina</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">OT</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Km</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Costo</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {history.map((item) => (
                                            <tr key={item.id} className="hover:bg-blue-50/10 transition-colors">
                                                <td className="px-4 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-slate-900 dark:text-slate-200 text-sm">{formatDate(item.fecha_realizado)}</span>
                                                        <span className="text-[10px] text-slate-400 capitalize">{formatDay(item.fecha_realizado)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className="font-bold text-slate-800 dark:text-slate-300 text-sm leading-tight">
                                                            {item.rutina_nombre || item.tipo}
                                                        </span>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800 w-fit">
                                                            {item.tipo}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div
                                                        onClick={() => onViewOt && onViewOt(item.id)}
                                                        className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-bold text-xs bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5 rounded-lg w-fit cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:scale-105 transition-all border border-blue-100 dark:border-blue-800"
                                                    >
                                                        <FileText className="h-3 w-3" />
                                                        {item.codigo_ot}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-sm">
                                                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 font-medium tracking-tight">
                                                        <Gauge className="h-3.5 w-3.5" />
                                                        {item.kilometraje.toLocaleString()} km
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-right">
                                                    <div className="flex items-center justify-end gap-1 font-bold text-slate-900 dark:text-slate-100">
                                                        <span className="text-green-600 mr-0.5">S/</span>
                                                        {Number(item.costo_total || 0).toLocaleString('es-PE', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-end gap-3 rounded-b-lg">
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={exportToExcel} className="hidden sm:flex" disabled={history.length === 0}>
                            <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                            Excel
                        </Button>
                        <Button variant="default" onClick={previewPDF} className="bg-slate-900 hover:bg-slate-800 text-white hidden sm:flex" disabled={history.length === 0}>
                            <FileText className="w-4 h-4 mr-2" />
                            Vista Previa PDF
                        </Button>
                        <Button variant="outline" onClick={exportToPDF} className="border-slate-300 hidden sm:flex" disabled={history.length === 0}>
                            <Download className="w-4 h-4 mr-2" />
                            Descargar
                        </Button>
                        <Button variant="ghost" onClick={onClose} className="px-8 h-10 font-bold hover:bg-slate-100 dark:hover:bg-slate-800">
                            Cerrar
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
